import * as Sentry from "@sentry/browser";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogHeader,
  Radio,
  RadioGroup,
  Switch,
} from "@stacklet/ui";
import { graphql, useFragment, useMutation } from "react-relay";

import { useAlertContext, useConfig } from "app/contexts";
import {
  API_EXPLORER_USER_DATA_NAME,
  parseApiExplorer,
  useApiExplorer,
} from "app/contexts/ApiExplorerContext"; // avoiding barrel export to satisfy relay
import {
  COLOR_SCHEME_USER_DATA_NAME,
  parseColorScheme,
  useTheme,
} from "app/contexts/ThemeContext"; // avoiding barrel export to satisfy relay
import { useProblemsAlert } from "app/hooks/ui/useProblemsAlert";
import { UpsertUserDataMutation } from "app/mutations";

import type { PreferencesDialog_query$key } from "./__generated__/PreferencesDialog_query.graphql";
import type { UpsertUserDataMutation as UpsertUserDataMutationType } from "app/mutations/__generated__/UpsertUserDataMutation.graphql";

interface Props {
  /** Sets the dialog's open state (e.g. to close it after a successful save). */
  openDialog(arg: boolean): void;
  /** Whether the dialog is currently open. */
  isDialogOpen: boolean;
  /** Bootstrap query ref carrying the user's preferences. */
  queryRef: PreferencesDialog_query$key;
}

export function PreferencesDialog({
  openDialog,
  isDialogOpen,
  queryRef,
}: Props) {
  const config = useConfig();

  const data = useFragment(
    graphql`
      fragment PreferencesDialog_query on Query {
        analytics: userData(
          filterElement: {
            single: {
              name: "name"
              operator: "equals"
              value: "analytics_enabled"
            }
          }
        ) {
          edges {
            node {
              value
            }
          }
          ...useProblemsAlert_problems
        }
      }
    `,
    queryRef,
  );

  useProblemsAlert(data.analytics);

  const analyticsEnabled = Boolean(
    (data.analytics.edges ?? []).find((edge) => edge.node.value === "true"),
  );

  // Separate hooks so each action's in-flight state is independent — otherwise
  // changing the color scheme would flip the analytics switch to a spinner.
  // Declared ahead of the handlers below so each handler references an
  // already-initialized mutation rather than relying on hoisting.
  const [commitAnalytics, isAnalyticsInFlight] =
    useMutation<UpsertUserDataMutationType>(UpsertUserDataMutation);
  const [commitColorScheme] = useMutation<UpsertUserDataMutationType>(
    UpsertUserDataMutation,
  );
  const [commitApiExplorer, isApiExplorerInFlight] =
    useMutation<UpsertUserDataMutationType>(UpsertUserDataMutation);

  const toggleAnalytics = () =>
    commitAnalytics({
      variables: {
        input: {
          name: "analytics_enabled",
          type: "config",
          value: String(!analyticsEnabled),
        },
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    });

  const { colorScheme, setColorScheme } = useTheme();

  const changeColorScheme = (value: string) => {
    const scheme = parseColorScheme(value) ?? "system";
    // Apply immediately for instant feedback, then persist so the preference
    // syncs across devices on next load.
    setColorScheme(scheme);
    commitColorScheme({
      variables: {
        input: {
          name: COLOR_SCHEME_USER_DATA_NAME,
          type: "config",
          value: scheme,
        },
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    });
  };

  const { alertDispatch } = useAlertContext();

  const { apiExplorer, setApiExplorer } = useApiExplorer();

  const changeApiExplorer = (value: string) => {
    const explorer = parseApiExplorer(value) ?? "apollo";
    const previous = apiExplorer;

    // Roll back so the radio reflects what is actually persisted and
    // re-selecting the option retries the save — an already-selected radio
    // emits no change event, so leaving the rejected value in place would
    // make "try again" impossible.
    const rollback = () => {
      setApiExplorer(previous);
      alertDispatch({
        message:
          "Something went wrong saving your API explorer preference. Please try again.",
        severity: "error",
      });
    };

    // Apply immediately for instant feedback, then persist so the preference
    // syncs across devices on next load.
    setApiExplorer(explorer);
    commitApiExplorer({
      variables: {
        input: {
          name: API_EXPLORER_USER_DATA_NAME,
          type: "config",
          value: explorer,
        },
      },
      // Field-level GraphQL errors arrive in a successful response via
      // onCompleted, not onError.
      onCompleted: (_response, errors) => {
        if (errors) {
          rollback();
        }
      },
      onError: (err) => {
        Sentry.captureException(err);
        rollback();
      },
    });
  };

  return (
    <Dialog
      data-testid="preferences-dialog"
      isOpen={isDialogOpen}
      onOpenChange={openDialog}
      size="lg"
      isDismissable
    >
      <DialogHeader>Preferences</DialogHeader>

      <DialogContent>
        <RadioGroup
          assistiveText="“System” follows your operating system's light or dark setting"
          className="mb-2xl"
          label="Appearance"
          onChange={changeColorScheme}
          orientation="horizontal"
          value={colorScheme}
        >
          <Radio value="light">Light</Radio>
          <Radio value="dark">Dark</Radio>
          <Radio value="system">System</Radio>
        </RadioGroup>

        {/* Disabled while a save is in flight so saves cannot overlap: a
            stale failure's rollback would otherwise clobber a newer
            selection. */}
        <RadioGroup
          isDisabled={isApiExplorerInFlight}
          label="API explorer"
          onChange={changeApiExplorer}
          orientation="horizontal"
          value={apiExplorer}
        >
          <Radio value="apollo">Apollo Sandbox</Radio>
          <Radio value="graphiql">GraphiQL</Radio>
        </RadioGroup>

        {/* Spacing lives here as a top margin (not a bottom margin on the API
            explorer group above) so the dialog has no trailing whitespace when
            this section is hidden. */}
        {config?.analytics_configurable ? (
          <div className="mt-2xl flex flex-col gap-[.625rem]">
            <span className="text-label-small text-text-secondary">
              Analytics
            </span>
            <div className="flex items-center justify-between gap-xl">
              <span>
                Enable analytics to shape improvements to the Stacklet console
              </span>
              {isAnalyticsInFlight ? (
                <CircularProgress size="small" />
              ) : (
                <Switch
                  isSelected={analyticsEnabled}
                  onChange={toggleAnalytics}
                />
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
